import { useEffect, useRef } from 'react';
import type { FieldPath } from 'react-hook-form';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  useCreateCoverageMutation,
  useDeleteCoverageMutation,
  useGetCoveragesQuery,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import type { ImportedCoverageEntry } from '@/services/types/case-ingestion-assist-types';
import type {
  IngestionSection,
  IngestionSections,
  IngestionSectionStatus,
} from '@/services/types/case-ingestion-types';
import {
  BenefitType,
  CoverageStatus,
  type CreateCoverageSchema,
  CoverageCategory as ApiCoverageCategory,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';

import { useUnifiedFlow } from '../../context/UnifiedFlowContext';
import { useResetIngestionSections, useStepSave } from '../../hooks';
import { CoverageSection } from './CoverageEntryCard';
import { IngestionStepLayout } from '../../layout';
import { CoverageStepSkeleton } from '../../skeletons';

// Export types for child components
export type CoverageCategory = 'firstParty' | 'thirdParty' | 'health';

const coverageEntrySchema = z.object({
  existingId: z.string().nullable().optional(), // Tracks existing coverage ID to prevent duplicates
  policyType: z.nativeEnum(PolicyType).nullable(),
  carrierName: z.string().optional(),
  carrierId: z.string().nullable().optional(),
  divisionId: z.string().nullable().optional(),
  policyNumber: z.string().optional(),
  namedInsured: z.string().optional(),
  adjusterName: z.string().optional(),
  adjusterPhone: z.string().optional(),
  adjusterEmail: z.string().optional(),
  adjusterFax: z.string().optional(),
  claimNumber: z.string().optional(),
  benefitType: z.nativeEnum(BenefitType).nullable(),
  policyLimit: z.string().optional(),
});

const formSchema = z.object({
  firstPartyNotFound: z.boolean(),
  firstPartyCoverages: z.array(coverageEntrySchema),

  thirdPartyNotFound: z.boolean(),
  thirdPartyCoverages: z.array(coverageEntrySchema),

  healthNotFound: z.boolean(),
  healthCoverages: z.array(coverageEntrySchema),
});

export type CoverageFormData = z.infer<typeof formSchema>;
type CoverageEntry = z.infer<typeof coverageEntrySchema>;
type CoverageArrayHandlers = {
  remove: (index: number) => void;
  insert: (index: number, value: CoverageEntry) => void;
};
type CoverageSectionKey =
  | 'firstPartyCoverages'
  | 'thirdPartyCoverages'
  | 'healthCoverages';
type CoveragePayload = Partial<CreateCoverageSchema>;

const serializeCoveragePayload = (payload: CoveragePayload) => JSON.stringify(payload);

const createEmptyCoverage = (category?: 'firstParty' | 'thirdParty' | 'health'): CoverageEntry => ({
  existingId: null,
  policyType: category === 'health' ? PolicyType.HEALTH : null,
  carrierName: '',
  carrierId: null,
  divisionId: null,
  policyNumber: '',
  namedInsured: '',
  adjusterName: '',
  adjusterPhone: '',
  adjusterEmail: '',
  adjusterFax: '',
  claimNumber: '',
  benefitType: null,
  policyLimit: '',
});

const buildCoveragePayload = (
  entry: CoverageEntry,
  category: ApiCoverageCategory,
): CoveragePayload => {
  const benefits = [];
  if (entry.benefitType && entry.policyLimit) {
    const limitNum = parseFloat(entry.policyLimit.replace(/[^0-9.]/g, ''));
    if (!Number.isNaN(limitNum)) {
      benefits.push({
        benefit_type: entry.benefitType,
        limitations: { limit: limitNum },
        is_stacked: null,
      });
    }
  }

  // Build adjuster handles array from contact fields
  const adjusterHandles: Array<{ handle_type: string; value: string }> = [];
  if (entry.adjusterPhone?.trim()) {
    adjusterHandles.push({ handle_type: 'PHONE', value: entry.adjusterPhone.trim() });
  }
  if (entry.adjusterEmail?.trim()) {
    adjusterHandles.push({ handle_type: 'EMAIL', value: entry.adjusterEmail.trim() });
  }
  if (entry.adjusterFax?.trim()) {
    adjusterHandles.push({ handle_type: 'FAX', value: entry.adjusterFax.trim() });
  }

  // Adjuster exists if name OR any contact info provided
  const hasAdjusterData = entry.adjusterName?.trim() || adjusterHandles.length > 0;

  return {
    category,
    policy_type: entry.policyType || PolicyType.AUTO,
    carrier: {
      name: entry.carrierName || '',
      handles: null,
      address: null,
    },
    carrier_v2_id: entry.carrierId || null,
    carrier_division_id: entry.divisionId || null,
    policy_identifiers: entry.policyNumber
      ? [
          {
            identifier_type: IdentifierType.POLICY_NUMBER,
            identifier_value: entry.policyNumber,
          },
        ]
      : null,
    named_insured: entry.namedInsured?.trim() ? [entry.namedInsured.trim()] : null,
    benefits: benefits.length > 0 ? benefits : null,
    adjusters: hasAdjusterData
      ? [
          {
            benefit_type: entry.benefitType || null,
            name: entry.adjusterName?.trim() || null,
            handles: adjusterHandles.length > 0 ? adjusterHandles : null,
          },
        ]
      : null,
    claims: entry.claimNumber
      ? [
          {
            benefit_type: entry.benefitType || null,
            claim_identifier: entry.claimNumber,
          },
        ]
      : null,
    vehicles: null,
    policy_details: null,
    subrogator: null,
  };
};

interface CoverageStepProps {
  matterId: string;
  sectionStatuses: IngestionSections;
  onSaveComplete?: (success: boolean) => void;
}

export const CoverageStep = ({
  matterId,
  sectionStatuses,
  onSaveComplete,
}: CoverageStepProps) => {
  const [createCoverage] = useCreateCoverageMutation();
  const [deleteCoverage] = useDeleteCoverageMutation();
  const [updateCoverage] = useUpdateCoverageMutation();

  const { saveStep } = useStepSave();
  const { resetSections } = useResetIngestionSections();

  // Initialize form with notFound values from sectionStatuses (guaranteed loaded via parent gate)
  const { control, handleSubmit, watch, setValue } = useForm<CoverageFormData>({
    defaultValues: {
      firstPartyNotFound: sectionStatuses.first_party_coverage === 'not_found',
      firstPartyCoverages: [],

      thirdPartyNotFound: sectionStatuses.third_party_coverage === 'not_found',
      thirdPartyCoverages: [],

      healthNotFound: sectionStatuses.health_insurance === 'not_found',
      healthCoverages: [],
    },
  });

  // Field arrays for each category
  const firstPartyArray = useFieldArray({
    control,
    name: 'firstPartyCoverages',
  });

  const thirdPartyArray = useFieldArray({
    control,
    name: 'thirdPartyCoverages',
  });

  const healthArray = useFieldArray({
    control,
    name: 'healthCoverages',
  });

  const savedCoverageSnapshots = useRef<Record<string, string>>({});

  const setExistingId = (
    sectionKey: CoverageSectionKey,
    index: number,
    coverageId: string,
  ) => {
    setValue(
      `${sectionKey}.${index}.existingId` as FieldPath<CoverageFormData>,
      coverageId,
    );
  };

  // Fetch existing coverages
  const { data: existingCoverages, isLoading: isLoadingCoverages } = useGetCoveragesQuery(
    { matterId },
    { skip: !matterId },
  );

  // Track if we've already hydrated coverages to prevent duplicate entries on refetch
  const hasHydrated = useRef(false);

  // Hydrate form from existing coverages
  // NOTE: This hook MUST be called before the early return below (React Rules of Hooks)
  useEffect(() => {
    if (!existingCoverages) return;
    if (hasHydrated.current) return;

    hasHydrated.current = true;

    const activeCoverages = existingCoverages.filter(
      (coverage) => coverage.status !== CoverageStatus.REJECTED,
    );

    if (activeCoverages.length === 0) return;

    const mapCoverageToEntry = (coverage: typeof existingCoverages[0]): CoverageEntry => {
      const policyIdentifier = coverage.policy_identifiers?.find(
        (pi) => pi.identifier_type === IdentifierType.POLICY_NUMBER,
      );
      const benefit = coverage.benefits?.[0];
      const adjuster = coverage.adjusters?.[0];

      // Extract adjuster handles by type
      const adjusterPhone = adjuster?.handles?.find(h => h.handle_type === 'PHONE')?.value || '';
      const adjusterEmail = adjuster?.handles?.find(h => h.handle_type === 'EMAIL')?.value || '';
      const adjusterFax = adjuster?.handles?.find(h => h.handle_type === 'FAX')?.value || '';

      return {
        existingId: coverage.id, // Preserve existing ID to prevent duplicate creation
        policyType: coverage.policy_type || null,
        carrierName: coverage.carrier?.name || '',
        carrierId: coverage.carrier_v2_id || null,
        divisionId: coverage.carrier_division_id || null,
        policyNumber: policyIdentifier?.identifier_value || '',
        namedInsured: coverage.named_insured?.[0] || '',
        adjusterName: adjuster?.name || '',
        adjusterPhone,
        adjusterEmail,
        adjusterFax,
        claimNumber: coverage.claims?.[0]?.claim_identifier || '',
        benefitType: benefit?.benefit_type || null,
        policyLimit: benefit?.limitations?.limit
          ? `$${benefit.limitations.limit.toLocaleString()}`
          : '',
      };
    };

    const mapCoveragesToEntries = (
      coverages: typeof activeCoverages,
      category: ApiCoverageCategory,
    ) =>
      coverages.map((coverage) => {
        const entry = mapCoverageToEntry(coverage);
        savedCoverageSnapshots.current[coverage.id] = serializeCoveragePayload(
          buildCoveragePayload(entry, category),
        );
        return entry;
      });

    const firstParty = activeCoverages.filter(
      (c) => c.category === ApiCoverageCategory.FIRST_PARTY,
    );
    const thirdParty = activeCoverages.filter(
      (c) => c.category === ApiCoverageCategory.THIRD_PARTY,
    );
    const health = activeCoverages.filter(
      (c) => c.category === ApiCoverageCategory.FIRST_PARTY_HEALTH,
    );

    if (firstParty.length) {
      setValue(
        'firstPartyCoverages',
        mapCoveragesToEntries(firstParty, ApiCoverageCategory.FIRST_PARTY),
      );
    }
    if (thirdParty.length) {
      setValue(
        'thirdPartyCoverages',
        mapCoveragesToEntries(thirdParty, ApiCoverageCategory.THIRD_PARTY),
      );
    }
    if (health.length) {
      setValue(
        'healthCoverages',
        mapCoveragesToEntries(health, ApiCoverageCategory.FIRST_PARTY_HEALTH),
      );
    }
  }, [existingCoverages, setValue]);

  // Auto-fill from CMS import data (runs once after hydration)
  const { importData } = useUnifiedFlow();
  const hasAutoFilledFromImport = useRef(false);
  useEffect(() => {
    if (hasAutoFilledFromImport.current) return;
    if (!importData?.payload?.coverage) return;
    // Wait for hydration to complete
    if (existingCoverages === undefined) return;
    // Don't prefill if user already has coverage entries
    if (existingCoverages.length > 0) return;

    hasAutoFilledFromImport.current = true;
    const cov = importData.payload.coverage;

    const mapImportedToEntry = (
      entry: ImportedCoverageEntry,
      category: 'firstParty' | 'thirdParty' | 'health',
    ): CoverageEntry => ({
      existingId: null,
      policyType: category === 'health' ? PolicyType.HEALTH : null,
      carrierName: entry.carrier_name || '',
      carrierId: null,
      divisionId: null,
      policyNumber: entry.policy_number || '',
      namedInsured: '',
      adjusterName: entry.adjuster_name || '',
      adjusterPhone: entry.adjuster_phone || '',
      adjusterEmail: '',
      adjusterFax: '',
      claimNumber: entry.claim_number || '',
      benefitType: null,
      policyLimit: '',
    });

    if (cov.first_party?.presence === 'present' && cov.first_party.value?.length) {
      setValue(
        'firstPartyCoverages',
        cov.first_party.value.map((e) => mapImportedToEntry(e, 'firstParty')),
      );
    }
    if (cov.third_party?.presence === 'present' && cov.third_party.value?.length) {
      setValue(
        'thirdPartyCoverages',
        cov.third_party.value.map((e) => mapImportedToEntry(e, 'thirdParty')),
      );
    }
    if (cov.health_insurance?.presence === 'present' && cov.health_insurance.value?.length) {
      setValue(
        'healthCoverages',
        cov.health_insurance.value.map((e) => mapImportedToEntry(e, 'health')),
      );
    }
  }, [importData, existingCoverages, setValue]);

  // Show skeleton during initial load (AFTER all hooks)
  if (isLoadingCoverages && !hasHydrated.current) {
    return <CoverageStepSkeleton />;
  }

  const firstPartyNotFound = watch('firstPartyNotFound');
  const thirdPartyNotFound = watch('thirdPartyNotFound');
  const healthNotFound = watch('healthNotFound');

  const firstPartyCoverages = watch('firstPartyCoverages');
  const thirdPartyCoverages = watch('thirdPartyCoverages');
  const healthCoverages = watch('healthCoverages');

  const removeCoverageEntry = async (
    index: number,
    entries: CoverageEntry[],
    arrayHandlers: CoverageArrayHandlers,
    section: IngestionSection,
  ) => {
    const entry = entries[index];
    if (!entry) return;

    const isLastEntry = entries.length === 1;
    arrayHandlers.remove(index);

    if (!entry.existingId) {
      if (isLastEntry) {
        try {
          await resetSections([section]);
        } catch {
          toast.error('Failed to reset coverage section');
        }
      }
      return;
    }

    try {
      await deleteCoverage({ matterId, coverageId: entry.existingId }).unwrap();
      delete savedCoverageSnapshots.current[entry.existingId];
    } catch {
      toast.error('Failed to delete coverage');
      arrayHandlers.insert(index, entry);
      return;
    }

    if (isLastEntry) {
      try {
        await resetSections([section]);
      } catch {
        toast.error('Failed to reset coverage section');
      }
    }
  };

  const handleRemoveFirstParty = (index: number) => {
    void removeCoverageEntry(
      index,
      firstPartyCoverages,
      firstPartyArray,
      'first_party_coverage',
    );
  };

  const handleRemoveThirdParty = (index: number) => {
    void removeCoverageEntry(
      index,
      thirdPartyCoverages,
      thirdPartyArray,
      'third_party_coverage',
    );
  };

  const handleRemoveHealth = (index: number) => {
    void removeCoverageEntry(
      index,
      healthCoverages,
      healthArray,
      'health_insurance',
    );
  };

  const isStepComplete = () => {
    // Step is complete when all sections are either filled or marked not-found
    const firstPartyDone =
      firstPartyNotFound ||
      firstPartyCoverages.some((c) => c.carrierName?.trim() && c.policyType);
    const thirdPartyDone =
      thirdPartyNotFound ||
      thirdPartyCoverages.some((c) => c.carrierName?.trim() && c.policyType);
    const healthDone =
      healthNotFound ||
      healthCoverages.some((c) => c.carrierName?.trim() && c.policyType);

    return firstPartyDone && thirdPartyDone && healthDone;
  };

  const handleFirstPartyNotFoundChange = async (checked: boolean) => {
    setValue('firstPartyNotFound', checked);
    if (checked) {
      const entriesToRestore = firstPartyCoverages;
      const coverageIds = entriesToRestore
        .filter((c) => c.existingId)
        .map((c) => c.existingId as string);

      setValue('firstPartyCoverages', []);
      if (coverageIds.length === 0) return;

      try {
        await Promise.all(
          coverageIds.map((coverageId) =>
            deleteCoverage({ matterId, coverageId }).unwrap(),
          ),
        );
        coverageIds.forEach((coverageId) => {
          delete savedCoverageSnapshots.current[coverageId];
        });
      } catch {
        toast.error('Failed to delete coverage');
        setValue('firstPartyNotFound', false);
        setValue('firstPartyCoverages', entriesToRestore);
      }
    }
  };

  const handleThirdPartyNotFoundChange = async (checked: boolean) => {
    setValue('thirdPartyNotFound', checked);
    if (checked) {
      const entriesToRestore = thirdPartyCoverages;
      const coverageIds = entriesToRestore
        .filter((c) => c.existingId)
        .map((c) => c.existingId as string);

      setValue('thirdPartyCoverages', []);
      if (coverageIds.length === 0) return;

      try {
        await Promise.all(
          coverageIds.map((coverageId) =>
            deleteCoverage({ matterId, coverageId }).unwrap(),
          ),
        );
        coverageIds.forEach((coverageId) => {
          delete savedCoverageSnapshots.current[coverageId];
        });
      } catch {
        toast.error('Failed to delete coverage');
        setValue('thirdPartyNotFound', false);
        setValue('thirdPartyCoverages', entriesToRestore);
      }
    }
  };

  const handleHealthNotFoundChange = async (checked: boolean) => {
    setValue('healthNotFound', checked);
    if (checked) {
      const entriesToRestore = healthCoverages;
      const coverageIds = entriesToRestore
        .filter((c) => c.existingId)
        .map((c) => c.existingId as string);

      setValue('healthCoverages', []);
      if (coverageIds.length === 0) return;

      try {
        await Promise.all(
          coverageIds.map((coverageId) =>
            deleteCoverage({ matterId, coverageId }).unwrap(),
          ),
        );
        coverageIds.forEach((coverageId) => {
          delete savedCoverageSnapshots.current[coverageId];
        });
      } catch {
        toast.error('Failed to delete coverage');
        setValue('healthNotFound', false);
        setValue('healthCoverages', entriesToRestore);
      }
    }
  };

  const onSubmit = async (data: CoverageFormData) => {
    // Determine section-level statuses (simplified)
    const hasFirstPartyCoverage = data.firstPartyCoverages.some((c) => c.carrierName?.trim());
    const hasThirdPartyCoverage = data.thirdPartyCoverages.some((c) => c.carrierName?.trim());
    const hasHealthCoverage = data.healthCoverages.some((c) => c.carrierName?.trim());

    // Only update section status if user made an explicit choice:
    // - Checked "not found" checkbox → 'not_found'
    // - Added coverage data → 'found'
    // - Neither → don't update (stays null/unreviewed)
    const sectionUpdates: Partial<Record<IngestionSection, IngestionSectionStatus>> = {};

    if (data.firstPartyNotFound) {
      sectionUpdates.first_party_coverage = 'not_found';
    } else if (hasFirstPartyCoverage) {
      sectionUpdates.first_party_coverage = 'found';
    }

    if (data.thirdPartyNotFound) {
      sectionUpdates.third_party_coverage = 'not_found';
    } else if (hasThirdPartyCoverage) {
      sectionUpdates.third_party_coverage = 'found';
    }

    if (data.healthNotFound) {
      sectionUpdates.health_insurance = 'not_found';
    } else if (hasHealthCoverage) {
      sectionUpdates.health_insurance = 'found';
    }

    // Count new entries (without existingId) that would be filtered due to missing carrierName
    const countNewEntriesMissingCarrier = (entries: CoverageEntry[]) =>
      entries.filter((e) => !e.existingId && !e.carrierName?.trim()).length;

    const skippedFirst = data.firstPartyNotFound ? 0 : countNewEntriesMissingCarrier(data.firstPartyCoverages);
    const skippedThird = data.thirdPartyNotFound ? 0 : countNewEntriesMissingCarrier(data.thirdPartyCoverages);
    const skippedHealth = data.healthNotFound ? 0 : countNewEntriesMissingCarrier(data.healthCoverages);
    const totalSkipped = skippedFirst + skippedThird + skippedHealth;

    // Warn user about entries that will be skipped
    if (totalSkipped > 0) {
      toast.warning(`${totalSkipped} coverage(s) skipped - Carrier Name is required`);
    }

    // Collect coverages to create
    const coveragesToCreate: Array<{
      entry: CoverageEntry;
      category: ApiCoverageCategory;
      sectionKey: CoverageSectionKey;
      index: number;
    }> = [];

    // Collect first party coverages (only new entries without existingId and with carrierName)
    if (!data.firstPartyNotFound) {
      data.firstPartyCoverages.forEach((entry, index) => {
        if (!entry.carrierName?.trim() || entry.existingId) return;
        coveragesToCreate.push({
          entry,
          category: ApiCoverageCategory.FIRST_PARTY,
          sectionKey: 'firstPartyCoverages',
          index,
        });
      });
    }

    // Collect third party coverages (only new entries without existingId and with carrierName)
    if (!data.thirdPartyNotFound) {
      data.thirdPartyCoverages.forEach((entry, index) => {
        if (!entry.carrierName?.trim() || entry.existingId) return;
        coveragesToCreate.push({
          entry,
          category: ApiCoverageCategory.THIRD_PARTY,
          sectionKey: 'thirdPartyCoverages',
          index,
        });
      });
    }

    // Collect health coverages (only new entries without existingId and with carrierName)
    if (!data.healthNotFound) {
      data.healthCoverages.forEach((entry, index) => {
        if (!entry.carrierName?.trim() || entry.existingId) return;
        coveragesToCreate.push({
          entry,
          category: ApiCoverageCategory.FIRST_PARTY_HEALTH,
          sectionKey: 'healthCoverages',
          index,
        });
      });
    }

    const coveragesToUpdate: Array<{
      coverageId: string;
      payload: CoveragePayload;
      payloadKey: string;
    }> = [];

    const trackUpdatesForEntries = (
      entries: CoverageEntry[],
      category: ApiCoverageCategory,
    ) => {
      entries.forEach((entry) => {
        if (!entry.existingId) return;
        const coverageId = entry.existingId as string;
        const payload = buildCoveragePayload(entry, category);
        const payloadKey = serializeCoveragePayload(payload);
        if (savedCoverageSnapshots.current[coverageId] !== payloadKey) {
          coveragesToUpdate.push({ coverageId, payload, payloadKey });
        }
      });
    };

    if (!data.firstPartyNotFound) {
      trackUpdatesForEntries(data.firstPartyCoverages, ApiCoverageCategory.FIRST_PARTY);
    }
    if (!data.thirdPartyNotFound) {
      trackUpdatesForEntries(data.thirdPartyCoverages, ApiCoverageCategory.THIRD_PARTY);
    }
    if (!data.healthNotFound) {
      trackUpdatesForEntries(data.healthCoverages, ApiCoverageCategory.FIRST_PARTY_HEALTH);
    }

    // Determine accurate success message
    const totalCreated = coveragesToCreate.length;
    const totalUpdated = coveragesToUpdate.length;
    const successMessage =
      totalCreated > 0 || totalUpdated > 0
        ? `Coverage step saved${totalCreated > 0 ? ` (${totalCreated} created)` : ''}${totalUpdated > 0 ? ` (${totalUpdated} updated)` : ''}`
        : 'Coverage step completed';

    const success = await saveStep(
      async () => {
        const createdCoverages = await Promise.all(
          coveragesToCreate.map(({ entry, category }) =>
            createCoverage({
              matterId,
              coverage: buildCoveragePayload(entry, category),
            }).unwrap(),
          ),
        );

        createdCoverages.forEach((coverage, index) => {
          const { entry, category, sectionKey, index: entryIndex } =
            coveragesToCreate[index];
          setExistingId(sectionKey, entryIndex, coverage.id);
          savedCoverageSnapshots.current[coverage.id] = serializeCoveragePayload(
            buildCoveragePayload(entry, category),
          );
        });

        if (coveragesToUpdate.length > 0) {
          await Promise.all(
            coveragesToUpdate.map(({ coverageId, payload }) =>
              updateCoverage({
                matterId,
                coverageId,
                coverage: payload,
              }).unwrap(),
            ),
          );
          coveragesToUpdate.forEach(({ coverageId, payloadKey }) => {
            savedCoverageSnapshots.current[coverageId] = payloadKey;
          });
        }
      },
      sectionUpdates,
      {
        successMessage,
        errorMessage: 'Failed to save coverage information',
      },
    );

    onSaveComplete?.(success);
  };

  return (
    <IngestionStepLayout
      title="Insurance Coverage"
      description="Enter insurance information for 1st Party, 3rd Party, and Health coverage"
    >
      <form
        id="ingestion-step-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-2"
      >
        {/* First Party Section */}
        <CoverageSection
          title="First Party (1P) Coverage"
          category="firstParty"
          control={control}
          setValue={setValue}
          fields={firstPartyArray.fields}
          onAdd={() => firstPartyArray.append(createEmptyCoverage('firstParty'))}
          onRemove={handleRemoveFirstParty}
          notFoundChecked={firstPartyNotFound}
          onNotFoundChange={handleFirstPartyNotFoundChange}
          notFoundLabel="Not found"
          addButtonLabel="Add 1P Coverage"
        />

        {/* Third Party Section */}
        <CoverageSection
          title="Third Party (3P) Coverage"
          category="thirdParty"
          control={control}
          setValue={setValue}
          fields={thirdPartyArray.fields}
          onAdd={() => thirdPartyArray.append(createEmptyCoverage('thirdParty'))}
          onRemove={handleRemoveThirdParty}
          notFoundChecked={thirdPartyNotFound}
          onNotFoundChange={handleThirdPartyNotFoundChange}
          notFoundLabel="Not found"
          addButtonLabel="Add 3P Coverage"
        />

        {/* Health Insurance Section */}
        <CoverageSection
          title="Health Insurance"
          category="health"
          control={control}
          setValue={setValue}
          fields={healthArray.fields}
          onAdd={() => healthArray.append(createEmptyCoverage('health'))}
          onRemove={handleRemoveHealth}
          notFoundChecked={healthNotFound}
          onNotFoundChange={handleHealthNotFoundChange}
          notFoundLabel="Not found"
          addButtonLabel="Add Health Insurance"
        />
      </form>
    </IngestionStepLayout>
  );
};
